import { useEffect, useState } from 'react'

/**
 * Enrutado por hash: funciona en cualquier hosting estático y en la PWA instalada. Las rutas
 * llevan el ID del arreglo cuando hace falta («#/tocar/arr_1a2b…»).
 */
export type Route =
  | { name: 'repertoire' }
  | { name: 'new' }
  | { name: 'play'; arrangementId: string }
  | { name: 'edit'; arrangementId: string }
  | { name: 'timing'; arrangementId: string }
  | { name: 'settings' }
  | { name: 'clockSpike' }
  | { name: 'deviceSpike' }

export const paths = {
  repertoire: '/',
  new: '/nueva',
  settings: '/ajustes',
  clockSpike: '/spike/reloj',
  deviceSpike: '/spike/dispositivo',
  play: (arrangementId: string) => `/tocar/${encodeURIComponent(arrangementId)}`,
  edit: (arrangementId: string) => `/editar/${encodeURIComponent(arrangementId)}`,
  timing: (arrangementId: string) => `/tiempos/${encodeURIComponent(arrangementId)}`,
} as const

/** Interpreta una ruta. Lo que no reconoce cae en el repertorio, que siempre existe. */
export function matchRoute(path: string): Route {
  const [pathname = '/'] = path.split('?')
  const parts = pathname.split('/').filter(Boolean)
  const [head, id] = parts
  const decoded = id === undefined ? undefined : safeDecode(id)
  if (parts.length === 1 && head === 'nueva') return { name: 'new' }
  if (parts.length === 1 && head === 'ajustes') return { name: 'settings' }
  if (parts.length === 2 && head === 'tocar' && decoded) {
    return { name: 'play', arrangementId: decoded }
  }
  if (parts.length === 2 && head === 'editar' && decoded) {
    return { name: 'edit', arrangementId: decoded }
  }
  if (parts.length === 2 && head === 'tiempos' && decoded) {
    return { name: 'timing', arrangementId: decoded }
  }
  if (parts.length === 2 && head === 'spike' && id === 'reloj') return { name: 'clockSpike' }
  if (parts.length === 2 && head === 'spike' && id === 'dispositivo') {
    return { name: 'deviceSpike' }
  }
  return { name: 'repertoire' }
}

function safeDecode(value: string): string | undefined {
  try {
    return decodeURIComponent(value)
  } catch {
    return undefined
  }
}

export function currentRoute(): string {
  const hash = window.location.hash.replace(/^#/, '')
  return hash.startsWith('/') ? hash : '/'
}

export function navigate(route: string): void {
  window.location.hash = route
}

/** Vuelve atrás en el historial; si no hay a dónde, va al repertorio. */
export function goBack(): void {
  if (window.history.length > 1) window.history.back()
  else navigate(paths.repertoire)
}

export function useRoute(): string {
  const [route, setRoute] = useState(currentRoute)
  useEffect(() => {
    const onChange = () => setRoute(currentRoute())
    window.addEventListener('hashchange', onChange)
    return () => window.removeEventListener('hashchange', onChange)
  }, [])
  return route
}
